import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../api/queryClient'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'

const LIMIT = 50
const LEVELS = ['decennale', 'biennale', 'gpa'] as const

/**
 * The open claims of a site or building and everything under it (spec
 * 2026-10-11-17), on its Contracts tab: title, guarantee level, age.
 */
export function AssetClaimsCard({ org, assetUid }: { org: string; assetUid: string }) {
  const { t } = useTranslation('contracts')
  const { t: ti } = useTranslation('issues')
  const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues', {
    params: {
      path: { orgCode: org },
      query: { type: 'claim', asset_subtree_uid: assetUid, assignee_uid: 'all', status: 'open,in_progress', limit: LIMIT },
    },
  })
  if (!data) return null
  const claims = data.data ?? []
  const total = data.total ?? claims.length
  const now = Date.now()

  return (
    <Card data-testid="contract-checklist-claims">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('claims_card.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {claims.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="contract-checklist-claims-empty">{t('claims_card.empty')}</p>
        ) : (
          <ul className="divide-y divide-border">
            {claims.map((claim) => {
              const level = (claim.properties as Record<string, unknown> | undefined)?.level
              const days = claim.created_at ? Math.max(0, Math.floor((now - new Date(claim.created_at).getTime()) / 86_400_000)) : null
              return (
                <li key={claim.uid} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2" data-testid={`contract-checklist-claim-${claim.uid}`}>
                  <Link
                    to="/$org/issues/$uid"
                    params={{ org, uid: claim.uid! }}
                    className="min-w-0 break-words text-sm font-medium text-foreground hover:text-primary hover:underline"
                  >
                    {claim.title}
                  </Link>
                  {typeof level === 'string' && (LEVELS as readonly string[]).includes(level) && (
                    <Badge variant="secondary" data-testid={`contract-checklist-claim-level-${claim.uid}`}>
                      {ti(`claim.level_${level}`)}
                    </Badge>
                  )}
                  {days !== null && (
                    <span className="ml-auto text-xs text-muted-foreground">{t('claims_card.age', { count: days })}</span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        {total > claims.length && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="contract-checklist-claims-truncated">
            {t('claims_card.truncated', { shown: claims.length, total })}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
